import { useState, lazy, Suspense } from 'react';
import { Routes, Route, Navigate, useLocation } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { useAppData } from '@/hooks/useAppData';
import { Sidebar } from '@/components/layout/Sidebar';
import { Header } from '@/components/layout/Header';
import { EventModal } from '@/components/events/EventModal';
import { StageDetailPanel } from '@/components/pipeline/StageDetailPanel';
import { ProtectedRoute } from '@/components/auth/ProtectedRoute';
import { LoginPage } from '@/components/auth/LoginPage';
import { OIDCCallback } from '@/components/auth/OIDCCallback';
import { ForbiddenPage } from '@/components/pages/ForbiddenPage';
import { HeaderDataProvider } from '@/contexts/HeaderDataContext';
import { Decision } from '@/types';

const DashboardPage = lazy(() => import('@/components/pages/DashboardPage').then(m => ({ default: m.DashboardPage })));
const EventsPage = lazy(() => import('@/components/pages/EventsPage').then(m => ({ default: m.EventsPage })));
const CoveragePage = lazy(() => import('@/components/pages/CoveragePage').then(m => ({ default: m.CoveragePage })));
const AlertsPage = lazy(() => import('@/components/pages/AlertsPage').then(m => ({ default: m.AlertsPage })));
const AnalyticsPage = lazy(() => import('@/components/pages/AnalyticsPage').then(m => ({ default: m.AnalyticsPage })));
const LivePage = lazy(() => import('@/components/pages/LivePage').then(m => ({ default: m.LivePage })));
const UserManagementPage = lazy(() => import('@/components/pages/UserManagementPage').then(m => ({ default: m.UserManagementPage })));
const AgentManagementPage = lazy(() => import('@/components/pages/AgentManagementPage').then(m => ({ default: m.AgentManagementPage })));
const OrgManagementPage = lazy(() => import('@/components/pages/OrgManagementPage').then(m => ({ default: m.OrgManagementPage })));
const AgentConfigPage = lazy(() => import('@/components/pages/AgentConfigPage'));
const CategoryManagementPage = lazy(() => import('@/components/pages/CategoryManagementPage').then(m => ({ default: m.CategoryManagementPage })));
const AdminAnalyticsPage = lazy(() => import('@/components/pages/AdminAnalyticsPage').then(m => ({ default: m.AdminAnalyticsPage })));
const ProfilePage = lazy(() => import('@/components/pages/ProfilePage').then(m => ({ default: m.ProfilePage })));
const ApiDocsPage = lazy(() => import('@/components/pages/ApiDocsPage').then(m => ({ default: m.ApiDocsPage })));
const APIKeysPage = lazy(() => import('@/components/pages/APIKeysPage').then(m => ({ default: m.APIKeysPage })));

function PageLoader() {
  return (
    <div className="flex items-center justify-center h-64">
      <div className="animate-spin w-8 h-8 border-2 border-primary border-t-transparent rounded-full" />
    </div>
  );
}

/** Authenticated shell with sidebar + header */
function AppShell() {
  const data = useAppData();
  const location = useLocation();
  const [selectedDecision, setSelectedDecision] = useState<Decision | null>(null);
  const [activeStagePanel, setActiveStagePanel] = useState<string | null>(null);
  const [sidebarCollapsed, setSidebarCollapsed] = useState(false);

  return (
    <div className="flex h-screen overflow-hidden bg-background">
      {/* Sidebar */}
      <Sidebar
        collapsed={sidebarCollapsed}
        onToggleCollapse={() => setSidebarCollapsed(!sidebarCollapsed)}
        counts={{
          relevant: data.stats?.relevant_count,
          all: data.stats?.total_decisions,
          raw: data.stats?.total_events,
          live: data.activity.length || undefined,
          cover: data.stats?.coverage_count,
          alerts: data.alerts.length || undefined,
        }}
      />

      {/* Main content area */}
      <div className="flex-1 flex flex-col overflow-hidden">
        <Header
          tenant={data.tenant}
          onTenantChange={data.setTenantAndReload}
        />

        <main className="flex-1 overflow-y-auto">
          <div className="max-w-[1600px] mx-auto px-6 py-6">
            <Suspense fallback={<PageLoader />}>
              <AnimatePresence mode="wait">
                <motion.div
                  key={location.pathname}
                  initial={{ opacity: 0, y: 8 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0, y: -8 }}
                  transition={{ duration: 0.2, ease: 'easeOut' }}
                >
                  <Routes>
                    <Route path="/" element={
                      <DashboardPage data={data} onStageClick={setActiveStagePanel} onEventClick={setSelectedDecision} />
                    } />
                    <Route path="/events" element={
                      <EventsPage data={{...data, currentTab: 'relevant'}} onEventClick={setSelectedDecision} />
                    } />
                    <Route path="/events/all" element={
                      <EventsPage data={{...data, currentTab: 'all'}} onEventClick={setSelectedDecision} />
                    } />
                    <Route path="/events/raw" element={
                      <EventsPage data={{...data, currentTab: 'raw'}} onEventClick={setSelectedDecision} />
                    } />
                    <Route path="/events/filtered" element={
                      <EventsPage data={{...data, currentTab: 'filtered'}} onEventClick={setSelectedDecision} />
                    } />
                    <Route path="/live" element={
                      <LivePage activity={data.activity} onRefresh={data.loadActivity} actionThreshold={data.actionThreshold} relevanceThreshold={data.relevanceThreshold} />
                    } />
                    <Route path="/coverage" element={
                      <CoveragePage data={data} />
                    } />
                    <Route path="/alerts" element={
                      <AlertsPage alerts={data.alerts} onRefresh={data.loadContent} />
                    } />
                    <Route path="/analytics" element={
                      <AnalyticsPage stats={data.stats} tenant={data.tenant} />
                    } />
                    <Route path="/admin/users" element={
                      <ProtectedRoute minRole="admin"><UserManagementPage /></ProtectedRoute>
                    } />
                    <Route path="/admin/agents" element={
                      <ProtectedRoute minRole="admin"><AgentManagementPage /></ProtectedRoute>
                    } />
                    <Route path="/admin/agents/:agentId/config" element={
                      <ProtectedRoute minRole="admin"><AgentConfigPage /></ProtectedRoute>
                    } />
                    <Route path="/admin/orgs" element={
                      <ProtectedRoute minRole="super_admin"><OrgManagementPage /></ProtectedRoute>
                    } />
                    <Route path="/admin/categories" element={
                      <ProtectedRoute minRole="admin"><CategoryManagementPage /></ProtectedRoute>
                    } />
                    <Route path="/admin/analytics" element={
                      <ProtectedRoute minRole="admin"><AdminAnalyticsPage /></ProtectedRoute>
                    } />
                    <Route path="/admin/api-keys" element={
                      <ProtectedRoute minRole="admin"><APIKeysPage /></ProtectedRoute>
                    } />
                    <Route path="/profile" element={<ProfilePage />} />
                    <Route path="/docs" element={<ApiDocsPage />} />
                    <Route path="/forbidden" element={<ForbiddenPage />} />
                    <Route path="*" element={<Navigate to="/" replace />} />
                  </Routes>
                </motion.div>
              </AnimatePresence>
            </Suspense>
          </div>
        </main>
      </div>

      {/* Overlays */}
      <StageDetailPanel
        activeStage={activeStagePanel}
        onClose={() => setActiveStagePanel(null)}
        stats={data.stats}
        actionThreshold={data.actionThreshold}
        relevanceThreshold={data.relevanceThreshold}
      />

      <EventModal
        decision={selectedDecision}
        onClose={() => setSelectedDecision(null)}
        onDecisionUpdated={(updated) => {
          setSelectedDecision(updated);
          data.updateDecision(updated);
        }}
      />
    </div>
  );
}

function App() {
  return (
    <Routes>
      <Route path="/login" element={<LoginPage />} />
      <Route path="/auth/callback" element={<OIDCCallback />} />
      <Route path="/*" element={
        <ProtectedRoute>
          <HeaderDataProvider>
            <AppShell />
          </HeaderDataProvider>
        </ProtectedRoute>
      } />
    </Routes>
  );
}

export default App;
